import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAppAuth, useAppTenantsState } from '../hooks/useAppAuth';
import { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import { SidebarProvider, SidebarInset } from '../components/ui/sidebar';
import AuthService from '../services/auth.service';
import FeatureFlagService from '../services/featureFlag.service';
import { FeatureFlag, useFeatureFlag } from '../services/featureFlag.service';
import { useWhitelabelOverrides } from '../hooks/useWhitelabelOverrides';
import { UnsavedChangesProvider } from '../contexts/UnsavedChangesContext';
import { useTenantConfig } from '../hooks/useTenantConfig';
import { ApiError } from '../services/api.service';

function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

export default function AuthenticatedRootPage() {
  const { isAuthenticated: fronteggAuthenticated, isLoading, user } = useAppAuth();
  const { tenants } = useAppTenantsState();
  const location = useLocation();
  useWhitelabelOverrides();
  const { data: tenantConfig, error: configError, isError: isConfigError } = useTenantConfig();

  // Check for system session auth (used by screengrabber/notifications)
  const hasSystemSession = !fronteggAuthenticated && !!AuthService.getInstance().getAuthState();
  const isAuthenticated = fronteggAuthenticated || hasSystemSession;

  // Sync Frontegg auth state with AuthService
  useEffect(() => {
    if (user && isAuthenticated) {
      // Create auth state from Frontegg
      // Map IRole[] to string[] for roles
      const roleNames = (user.roles || []).map(role =>
        typeof role === 'string' ? role : role.name || role.key || ''
      );
      const authState = {
        sub: user.id || '',
        name: user.name || '',
        email: user.email || '',
        tenant_id: user.tenantId || '',
        roles: roleNames,
        tenant_name: user.tenantId || 'Tenant',
        access_token: user.accessToken || '',
        profile_img_url: user.profilePictureUrl || undefined,
        tenants: [],
        customClaims: (user as any).customClaims || {},
      };
      AuthService.getInstance().setAuthState(authState);
      const currentTenant = tenants?.find((t: any) => t.tenantId === user.tenantId);
      const lowerRoles = roleNames.map(r => r.toLowerCase());
      const highestRole = lowerRoles.includes('admin') ? 'Admin'
        : lowerRoles.includes('builder') ? 'Builder'
        : lowerRoles.includes('readonly') ? 'ReadOnly'
        : lowerRoles.includes('readonly_partner') ? 'ReadOnly_Partner'
        : 'Unknown';
      FeatureFlagService.getInstance().identify({
        key: user.id || '',
        name: user.name || '',
        email: user.email || '',
        tenant: currentTenant?.name || 'Tenant',
        role: highestRole,
      });
    }
  }, [user, isAuthenticated]);

  // After SSO callback, check if we need to redirect back to a custom domain.
  // Track redirect state so we show a loading screen instead of flashing the app.
  const [redirectingToCustomDomain, setRedirectingToCustomDomain] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;

    // ?nocustom=1 escape hatch — skip custom domain redirect entirely
    const params = new URLSearchParams(window.location.search);
    if (params.get('nocustom') === '1') {
      // Clear the long-lived cookies so the user stays on the main domain
      document.cookie = 'plb_custom_origin=; Max-Age=0; path=/';
      document.cookie = 'plb_custom_origin_token=; Max-Age=0; path=/';
      document.cookie = 'plb_custom_origin_tenant=; Max-Age=0; path=/';
      return;
    }

    // Check short-lived cookies (set during current sso-bounce flow)
    let returnTo = getCookie('plb_sso_return_to') || localStorage.getItem('externalReturnTo');
    let returnToken = getCookie('plb_sso_return_token') || localStorage.getItem('externalReturnToken');

    // Fall back to long-lived cookies (survive logout, set by previous sso-bounce).
    if (!returnTo || !returnToken) {
      const customOrigin = getCookie('plb_custom_origin');
      const customOriginToken = getCookie('plb_custom_origin_token');
      if (customOrigin && customOriginToken) {
        const currentOrigin = window.location.origin;
        if (decodeURIComponent(customOrigin) !== currentOrigin && customOrigin !== currentOrigin) {
          returnTo = customOrigin;
          returnToken = customOriginToken;
        }
      }
    }

    const performRedirect = (url: string, token: string) => {
      setRedirectingToCustomDomain(true);
      // Clear all custom domain cookies to prevent re-triggering
      document.cookie = 'plb_sso_return_to=; Max-Age=0; path=/';
      document.cookie = 'plb_sso_return_token=; Max-Age=0; path=/';
      document.cookie = 'plb_custom_origin=; Max-Age=0; path=/';
      document.cookie = 'plb_custom_origin_token=; Max-Age=0; path=/';
      document.cookie = 'plb_custom_origin_tenant=; Max-Age=0; path=/';
      localStorage.removeItem('externalReturnTo');
      localStorage.removeItem('externalReturnToken');

      const doRedirect = (authCode?: string) => {
        let redirectUrl = `/api/v1/env/return?url=${encodeURIComponent(url)}&token=${encodeURIComponent(token)}`;
        if (authCode) {
          redirectUrl += `&auth_code=${encodeURIComponent(authCode)}`;
        }
        window.location.href = redirectUrl;
      };

      if (user?.accessToken) {
        fetch('/api/v1/env/auth-handoff', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${user.accessToken}` },
        })
          .then(r => r.ok ? r.json() : null)
          .then(data => doRedirect(data?.code))
          .catch(() => doRedirect());
      } else {
        doRedirect();
      }
    };

    if (returnTo && returnToken) {
      performRedirect(returnTo, returnToken);
      return;
    }

    // Safari ITP strips cookies set during redirect chains (bounce tracking
    // protection). Fall back to Redis-backed return info piggybacked on
    // the /api/v1/config response (already fetched by useTenantConfig).
    // Skip if we're already on the target domain (avoids redirect loop).
    const pending = (tenantConfig as any)?.pending_custom_return;
    if (pending?.url && pending?.token) {
      const pendingOrigin = new URL(pending.url).origin;
      if (pendingOrigin !== window.location.origin) {
        performRedirect(pending.url, pending.token);
      }
    }
  }, [isAuthenticated, tenantConfig]);

  // Redirect authenticated users to legacy frontend when force-legacy flag is enabled
  const forceLegacy = useFeatureFlag(FeatureFlag.FORCE_LEGACY);
  useEffect(() => {
    const authService = AuthService.getInstance();
    if (forceLegacy && authService.legacyAppUrl && isAuthenticated) {
      window.location.href = authService.legacyAppUrl;
    }
  }, [forceLegacy, isAuthenticated]);

  // Store return path for redirect after login
  useEffect(() => {
    if (location && !location.pathname.includes('/login') && !location.pathname.includes('/logout')) {
      const existingReturnPath = localStorage.getItem('returnPath');
      if (!existingReturnPath && location.hash !== '#returning') {
        localStorage.setItem('returnPath', location.pathname + location.search);
      }
    }
  }, [location]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    const authService = AuthService.getInstance();
    if (authService.useExternalLogin) {
      // Locally, Frontegg OAuth is configured for a different port, so redirect there for login
      window.location.href = `${authService.authAppUrl}/login`;
      return null;
    }
    return <Navigate to="/login" replace />;
  }

  // Show loading screen while redirecting back to custom domain
  if (redirectingToCustomDomain) {
    return (
      <div className="flex items-center justify-center h-screen bg-background">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (isConfigError && configError instanceof ApiError && configError.code === 404) {
    const payload = configError.payload as any;
    if (payload?.archived) {
      return <Navigate to="/tenant-archived" state={{ tenantName: payload.tenant_name }} replace />;
    }
    return <Navigate to="/tenant-not-found" replace />;
  }

  return (
    <SidebarProvider>
      <Sidebar />
      <SidebarInset>
        <UnsavedChangesProvider>
          <Outlet />
        </UnsavedChangesProvider>
      </SidebarInset>
    </SidebarProvider>
  );
}

